import React, { useEffect, useState, useRef, useMemo } from "react";
import { useLanguage } from "../LanguageContext";
import { TablePagination } from "../ui/table-pagination";
import { Plus, Edit2, Trash2, X } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Spinner } from "../ui/spinner";
import { db, Fee } from "../../lib/supabase";

export function FeesManager() {
  const [fees, setFees] = useState<Fee[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [total, setTotal] = useState(0);
  const [estadoFilter, setEstadoFilter] = useState<string>("");

  const sortedFees = useMemo(() => {
    if (!sortBy) return [...fees];
    const list = [...fees];
    const dir = sortDir === 'desc' ? -1 : 1;
    list.sort((a: Fee, b: Fee) => {
      switch (sortBy) {
        case 'concepto':
          return dir * String(a.concepto || '').localeCompare(String(b.concepto || ''), undefined, { sensitivity: 'base' });
        case 'cantidad':
          return dir * ((Number(a.cantidad) || 0) - (Number(b.cantidad) || 0));
        case 'fecha':
          return dir * (new Date(a.fecha || 0).getTime() - new Date(b.fecha || 0).getTime());
        case 'fecha_pago':
          return dir * ((a.fecha_pago ? new Date(a.fecha_pago).getTime() : 0) - (b.fecha_pago ? new Date(b.fecha_pago).getTime() : 0));
        case 'estado':
          return dir * String(a.estado || '').localeCompare(String(b.estado || ''), undefined, { sensitivity: 'base' });
        default:
          return 0;
      }
    });
    return list;
  }, [fees, sortBy, sortDir]);

  const pageSum = sortedFees.reduce((s, f) => s + Number(f.cantidad || 0), 0);

  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editing, setEditing] = useState<Fee | null>(null);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<Partial<Fee>>({ estado: "PENDING", fecha: today });

  const tableRef = useRef<HTMLTableElement | null>(null);
  const topScrollerRef = useRef<HTMLDivElement | null>(null);
  const topInnerRef = useRef<HTMLDivElement | null>(null);
  const bottomWrapperRef = useRef<HTMLDivElement | null>(null);
  const [showScrollHint, setShowScrollHint] = useState(false);

  useEffect(() => {
    fetchPage();
  }, [page, pageSize, estadoFilter]);

  useEffect(() => {
    const syncWidths = () => {
      const table = tableRef.current;
      const topInner = topInnerRef.current;
      const bottom = bottomWrapperRef.current;
      if (!table || !topInner || !bottom) return;
      topInner.style.width = `${table.scrollWidth}px`;
      const isMobile = window.innerWidth < 768;
      setShowScrollHint(isMobile || bottom.scrollWidth > bottom.clientWidth);
    };

    syncWidths();
    window.addEventListener("resize", syncWidths);

    const top = topScrollerRef.current;
    const bottom = bottomWrapperRef.current;
    if (top && bottom) {
      const onTopScroll = () => { if (bottom) bottom.scrollLeft = top.scrollLeft; };
      const onBottomScroll = () => { if (top) top.scrollLeft = bottom.scrollLeft; };
      top.addEventListener("scroll", onTopScroll);
      bottom.addEventListener("scroll", onBottomScroll);
      return () => {
        top.removeEventListener("scroll", onTopScroll);
        bottom.removeEventListener("scroll", onBottomScroll);
        window.removeEventListener("resize", syncWidths);
      };
    }

    return () => window.removeEventListener("resize", syncWidths);
  }, [fees.length]);

  async function fetchPage() {
    try {
      setLoading(true);
      const res = await db.getFeesPaged(page, pageSize, estadoFilter);
      setFees(res.data || []);
      setTotal(res.total || 0);
    } catch (e) {
      console.error("getFeesPaged error:", e);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreate() {
    if (!form.concepto || !form.cantidad || !form.fecha) {
      alert("Completa concepto, cantidad y fecha");
      return;
    }
    // fecha_pago must be >= fecha when provided
    if (form.fecha_pago && String(form.fecha_pago) < String(form.fecha)) {
      alert("La fecha de pago no puede ser anterior a la fecha de registro");
      return;
    }
    // if estado is PAID, fecha_pago is required
    if (String(form.estado).toUpperCase() === 'PAID' && !form.fecha_pago) {
      alert('Cuando el estado es PAID, la fecha de pago es obligatoria');
      return;
    }
    try {
      setLoading(true);
      await db.createFee({
        concepto: String(form.concepto),
        cantidad: Number(form.cantidad),
        fecha: String(form.fecha),
        estado: (form.estado as any) || "PENDING",
        fecha_pago: form.fecha_pago || null,
        link: form.link || null,
      });
      setIsCreateOpen(false);
      setForm({ estado: "PENDING", fecha: today });
      fetchPage();
    } catch (e) {
      console.error("createFee error:", e);
      alert("Error creando fee");
    } finally {
      setLoading(false);
    }
  }

  function openEdit(f: Fee) {
    setEditing(f);
    setForm({
      ...f,
      fecha: f.fecha ? String(f.fecha).slice(0, 10) : today,
      fecha_pago: f.fecha_pago ? String(f.fecha_pago).slice(0, 10) : undefined,
      link: f.link || undefined,
    });
    setIsEditOpen(true);
  }

  async function handleUpdate() {
    if (!editing) return;
    if (form.fecha_pago && String(form.fecha_pago) < String(form.fecha)) {
      alert("La fecha de pago no puede ser anterior a la fecha de registro");
      return;
    }
    if (String(form.estado).toUpperCase() === 'PAID' && !form.fecha_pago) {
      alert('Cuando el estado es PAID, la fecha de pago es obligatoria');
      return;
    }
    try {
      setLoading(true);
      await db.updateFee(editing.id, {
        concepto: String(form.concepto || editing.concepto),
        cantidad: Number(form.cantidad ?? editing.cantidad),
        fecha: String(form.fecha || editing.fecha),
        estado: (form.estado as any) || editing.estado,
        fecha_pago: form.fecha_pago ?? editing.fecha_pago ?? null,
        link: form.link ?? editing.link ?? null,
      });
      setIsEditOpen(false);
      setEditing(null);
      setForm({ estado: "PENDING", fecha: today });
      fetchPage();
    } catch (e) {
      console.error("updateFee error:", e);
      alert("Error actualizando fee");
    } finally {
      setLoading(false);
    }
  }

  async function handleDelete(id: number) {
    if (!confirm("Confirmar borrado?")) return;
    try {
      setLoading(true);
      await db.deleteFee(id);
      fetchPage();
    } catch (e) {
      console.error("deleteFee error:", e);
      alert("Error borrando fee");
    } finally {
      setLoading(false);
    }
  }

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR" }).format(v);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl" style={{ fontWeight: 600 }}>Fees</h1>
          <p className="text-gray-600">Gestión de cargos y comisiones</p>
        </div>
        <div className="flex items-center gap-3">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-600">{/* status filter label */}Estado:</label>
              <select
                className="h-8 border rounded px-2 bg-white"
                value={estadoFilter}
                onChange={(e) => { setEstadoFilter(e.target.value); setPage(1); }}
              >
                <option value="">Todos</option>
                <option value="PAID">Pagados</option>
                <option value="PENDING">Pendientes</option>
              </select>
            </div>
            <Button variant="black" onClick={() => setIsCreateOpen(true)}>
              <Plus size={16} className="mr-2" /> Nuevo fee
            </Button>
        </div>
      </div>

      <div className="bg-white border border-gray-100 rounded-lg">
        {showScrollHint && (
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 mb-2 md:hidden transition-opacity duration-300 opacity-100">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-500" aria-hidden="true">
              <path d="M3 12h18" />
              <path d="M7 6L3 12L7 18" />
              <path d="M17 6L21 12L17 18" />
            </svg>
            <span>Desliza horizontalmente</span>
          </div>
        )}
        <div ref={topScrollerRef} className="overflow-x-auto overflow-y-hidden" style={{ height: 16 }}>
          <div ref={topInnerRef} style={{ height: 1 }} />
        </div>
        <div ref={bottomWrapperRef} className="overflow-x-auto fees-wrapper w-full rounded-lg bg-white">
          <style>{`
            /* Fees table styles inspired by AdminActivos (softer borders) */
            .fees-table { width: 100%; table-layout: fixed; }
            .fees-colgroup col { width: auto; }

            @media (max-width: 640px) {
              /* On small screens keep table layout and let the wrapper handle horizontal scroll */
              .fees-table { width: 100%; table-layout: fixed; }
              .fees-table thead { display: table-header-group; }
              .fees-table tbody tr { display: table-row; }
              .fees-table tbody tr > td { display: table-cell; padding-left: 0.5rem !important; padding-right: 0.5rem !important; }
              .fees-table th, .fees-table td { white-space: nowrap; }
              .fees-colgroup col.col-cantidad, .fees-colgroup col.col-fecha, .fees-colgroup col.col-fechaPago, .fees-colgroup col.col-estado { display: table-column; width: auto !important; }
            }

            /* Scroll container behaviour: wrapper handles scroll and fits parent width */
            .fees-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100vw; box-sizing: border-box; }

            /* Use table-level horizontal scrolling inside the wrapper; do not truncate content */
            .fees-wrapper { overflow-x: auto; -webkit-overflow-scrolling: touch; width: 100%; max-width: 100vw; box-sizing: border-box; }
            .fees-table { width: max-content; min-width: 100%; table-layout: auto; }
            /* Force single-line cells so the table can grow horizontally and the wrapper shows an internal scrollbar */
            .fees-table th, .fees-table td { white-space: nowrap; overflow: visible; text-overflow: clip; }

            /* On very narrow screens allow cells to wrap rather than expanding the table */
            @media (max-width: 480px) {
              .fees-table th, .fees-table td { white-space: normal; }
            }

            @media (min-width: 768px) {
              .fees-colgroup col.col-concepto { width: 30% !important; }
              .fees-colgroup col.col-cantidad { width: 14% !important; }
              .fees-colgroup col.col-fecha { width: 12% !important; }
              .fees-colgroup col.col-fechaPago { width: 12% !important; }
              .fees-colgroup col.col-estado { width: 12% !important; }
              .fees-colgroup col.col-link { width: 10% !important; }
              .fees-colgroup col.col-actions { width: 10% !important; }
            }

            .fees-table th, .fees-table td { min-width: 0; max-width: 100%; padding-left: 0.75rem !important; padding-right: 0.75rem !important; }
            .fees-table thead th { text-align: left !important; border-bottom: 1px solid #f3f4f6; }
            .fees-table td .inline-flex { gap: 0.5rem; }
            .fees-table thead th:last-child, .fees-table td[data-label="Acciones"] { min-width: 120px !important; box-sizing: border-box; }

            /* Prevent gaps and ensure hover covers full row when scrolling */
            .fees-table { border-collapse: collapse; }
            .fees-table th, .fees-table td { background-clip: padding-box; }
            /* Ensure paddings don't increase layout width unexpectedly */
            .fees-table, .fees-table th, .fees-table td { box-sizing: border-box; }

            /* Prevent the page content from overflowing the viewport similar to applying this to main */
            main { max-width: 100vw; box-sizing: border-box; overflow-x: hidden; }
            .fees-table tbody tr:hover td { background-color: #F9FAFB; transition: background-color 120ms ease; }
          `}</style>

          <table ref={tableRef} className="table-auto divide-y divide-gray-100 fees-table">
            <colgroup className="fees-colgroup">
              <col className="col-concepto" />
              <col className="col-cantidad" />
              <col className="col-fecha" />
              <col className="col-fechaPago" />
                <col className="col-estado" />
                <col className="col-link" />
                <col className="col-actions" />
            </colgroup>
            <thead>
              <tr className="bg-white">
                <th onClick={() => { setSortBy('concepto'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Concepto {sortBy==='concepto' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                <th onClick={() => { setSortBy('cantidad'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Cantidad {sortBy==='cantidad' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                <th onClick={() => { setSortBy('fecha'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Fecha {sortBy==='fecha' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                <th onClick={() => { setSortBy('fecha_pago'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Fecha pago {sortBy==='fecha_pago' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                <th onClick={() => { setSortBy('estado'); setSortDir((s)=> s==='desc' ? 'asc' : 'desc'); }} className="text-left px-6 py-4 text-xs text-gray-700 uppercase cursor-pointer" style={{ fontWeight: 700 }}>Estado {sortBy==='estado' ? (sortDir==='desc' ? '↓' : '↑') : '↕'}</th>
                <th className="text-left px-6 py-4 text-xs text-gray-700 uppercase" style={{ fontWeight: 700 }}>Enlace</th>
                <th className="text-left px-6 py-4 text-xs text-gray-700 uppercase" style={{ fontWeight: 700 }}>Acciones</th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr>
                  <td className="px-6 py-6" colSpan={7}>Cargando...</td>
                </tr>
              ) : fees.length === 0 ? (
                <tr>
                  <td className="px-6 py-6" colSpan={7}>No hay fees</td>
                </tr>
              ) : (
                <>
                  {sortedFees.map((f) => (
                    <tr key={f.id} className="hover:bg-gray-50 transition-colors">
                      <td title={String(f.concepto)} className="px-6 py-4 text-sm font-semibold">{f.concepto}</td>
                      <td title={String(f.cantidad)} className="px-6 py-4 text-sm font-medium">{formatCurrency(Number(f.cantidad))}</td>
                      <td title={String(f.fecha)} className="px-6 py-4 text-sm">{f.fecha}</td>
                      <td title={String(f.fecha_pago || "-")} className="px-6 py-4 text-sm">{f.fecha_pago || "-"}</td>
                      <td title={String(f.estado)} className="px-6 py-4 text-sm">{(() => {
                        const isPaid = String(f.estado || "").toUpperCase() === "PAID";
                        const baseClasses = "inline-block text-sm px-3 py-1 rounded-full";
                        const paidClasses = "bg-black text-white";
                        const defaultClasses = "bg-gray-100 text-gray-800";
                        return <span className={`${baseClasses} ${isPaid ? paidClasses : defaultClasses}`}>{f.estado}</span>;
                      })()}</td>
                      <td
                        title={String(f.link || "-")}
                        className="px-6 py-4 text-sm cursor-pointer"
                        onClick={() => {
                          if (!f.link) return;
                          try {
                            window.open(String(f.link), "_blank", "noopener,noreferrer");
                          } catch (err) {
                            // fallback
                            const w = window.open("about:blank", "_blank");
                            if (w) w.location.href = String(f.link);
                          }
                        }}
                      >
                        {f.link ? (
                          <a
                            href={String(f.link)}
                            onClick={(e) => { e.preventDefault(); /* prevent SPA routing */ if (f.link) window.open(String(f.link), "_blank", "noopener,noreferrer"); }}
                            className="text-sm text-blue-600 hover:underline"
                            rel="noopener noreferrer"
                          >
                            Ver
                          </a>
                        ) : (
                          <span className="text-sm text-gray-500">-</span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-sm" data-label="Acciones">
                        <div className="inline-flex items-center gap-2 justify-end">
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0 min-w-0" onClick={() => openEdit(f)}>
                            <Edit2 size={14} />
                          </Button>
                          <Button variant="ghost" size="icon" className="h-8 w-8 p-0 min-w-0" onClick={() => handleDelete(f.id)}>
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}

                  {/* Total row for current page */}
                  <tr className="bg-gray-50 font-semibold">
                    <td className="px-6 py-4 text-sm">TOTAL</td>
                    <td className="px-6 py-4 text-sm">{formatCurrency(pageSum)}</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                    <td className="px-6 py-4 text-sm">&nbsp;</td>
                  </tr>
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <div className="text-sm text-gray-600 flex items-center gap-3">
          <span>Tamaño de página:</span>
          <select
            className="h-8 border rounded px-2 bg-white"
            value={pageSize}
            onChange={(e) => { const s = Number(e.target.value); setPageSize(s); setPage(1); }}
          >
            {[10,20,50,100].map((opt) => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
        <div>
          <TablePagination
            page={page}
            totalPages={Math.max(1, Math.ceil(total / pageSize))}
            pageSize={pageSize}
            hidePageSizeSelector
            onPrev={() => setPage((p) => Math.max(1, p - 1))}
            onNext={() => setPage((p) => p + 1)}
            onPageSizeChange={(s) => { setPageSize(s); setPage(1); }}
          />
        </div>
      </div>

      {/* Create Modal */}
      <Dialog open={isCreateOpen} onOpenChange={(open) => { if (!open) { setIsCreateOpen(false); setForm({ estado: 'PENDING' }); } else setIsCreateOpen(open); }}>
          <DialogContent className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
            <DialogHeader className="pb-6 border-b">
            <DialogTitle className="text-3xl text-[#0F172A]" style={{ fontWeight: 600 }}>Nuevo fee</DialogTitle>
            <p className="text-gray-600 mt-2">Complete los datos del cargo o comisión</p>
          </DialogHeader>

          {/* Status area when creating */}
          {loading ? (
            <div className="flex items-center gap-3 p-4">
              <Spinner size={18} />
              <div>
                <p className="text-sm font-medium text-gray-900">Procesando…</p>
                <p className="text-sm text-gray-600">Espere mientras se guarda el fee.</p>
              </div>
            </div>
          ) : null}

          <div className="space-y-6 py-6 px-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Detalles del fee</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label className="text-sm block">Concepto *</Label>
                <Input placeholder="Concepto" value={form.concepto || ""} onChange={(e) => setForm({ ...form, concepto: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>

              <div>
                <Label className="text-sm block">Cantidad (€) *</Label>
                <Input placeholder="Cantidad" type="number" value={form.cantidad as any || ""} onChange={(e) => setForm({ ...form, cantidad: Number(e.target.value) })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>

              <div>
                <Label className="text-sm block">Fecha *</Label>
                <Input type="date" placeholder="Fecha (YYYY-MM-DD)" value={form.fecha || ""} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>

              <div>
                <Label className="text-sm block">Estado</Label>
                <select
                  className="mt-2 h-10 w-full rounded-full bg-white px-4 text-sm border-0 shadow-sm"
                  value={form.estado}
                  onChange={(e) => {
                    const v = e.target.value as any;
                    if (v === 'PAID') {
                      setForm((prev) => ({ ...prev, estado: v, fecha_pago: prev.fecha_pago || prev.fecha || today }));
                    } else {
                      setForm((prev) => ({ ...prev, estado: v, fecha_pago: undefined }));
                    }
                  }}
                  disabled={loading}
                >
                  <option value="PENDING">PENDING</option>
                  <option value="PAID">PAID</option>
                </select>
              </div>

              {form.estado === 'PAID' && (
                <div>
                  <Label className="text-sm block">Fecha pago *</Label>
                  <Input
                    type="date"
                    placeholder="Fecha pago (obligatoria)"
                    min={String(form.fecha || today)}
                    value={form.fecha_pago || ""}
                    onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })}
                    className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm"
                    disabled={loading}
                    required
                  />
                </div>
              )}

              <div>
                <Label className="text-sm block">Enlace (URL)</Label>
                <Input placeholder="https://..." type="url" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-6 border-t border-gray-100 gap-3">
            <Button variant="outline" className="h-12 px-8" onClick={() => { if (loading) return; setIsCreateOpen(false); setForm({ estado: 'PENDING' }); }} disabled={loading}>Cancelar</Button>
            <Button variant="black" className="h-12 px-8" onClick={handleCreate} disabled={loading}>
              {loading ? (<span className="inline-flex items-center gap-2"><Spinner size={14} /> Creando…</span>) : 'Crear fee'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Modal */}
      <Dialog open={isEditOpen && Boolean(editing)} onOpenChange={(open) => { if (!open) { setIsEditOpen(false); setEditing(null); setForm({ estado: 'PENDING' }); } else setIsEditOpen(open); }}>
        <DialogContent className="w-full max-w-[520px] max-h-[90vh] overflow-y-auto text-gray-900 border border-transparent shadow-lg" style={{ backgroundColor: '#fafafa' }}>
          <DialogHeader className="pb-6 border-b">
            <div className="flex items-start justify-between">
              <div>
                <DialogTitle className="text-3xl text-[#0F172A]" style={{ fontWeight: 600 }}>Editar fee</DialogTitle>
                <p className="text-gray-600 mt-2">Modifica los datos del cargo o comisión</p>
              </div>
              <button onClick={() => { if (loading) return; setIsEditOpen(false); setEditing(null); setForm({ estado: 'PENDING' }); }} className="text-gray-400 hover:text-gray-700"><X size={20} /></button>
            </div>
          </DialogHeader>

          {/* Status area when saving */}
          {loading ? (
            <div className="flex items-center gap-3 p-4">
              <Spinner size={18} />
              <div>
                <p className="text-sm font-medium text-gray-900">Guardando…</p>
                <p className="text-sm text-gray-600">Se están guardando los cambios.</p>
              </div>
            </div>
          ) : null}

          <div className="space-y-6 py-6 px-2">
            <h3 className="text-lg font-semibold text-gray-900 mb-3">Detalles del fee</h3>
            <div className="grid grid-cols-1 gap-4">
              <div>
                <Label className="text-sm block">Concepto *</Label>
                <Input placeholder="Concepto" value={form.concepto || ""} onChange={(e) => setForm({ ...form, concepto: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>

              <div>
                <Label className="text-sm block">Cantidad (€) *</Label>
                <Input placeholder="Cantidad" type="number" value={form.cantidad as any || ""} onChange={(e) => setForm({ ...form, cantidad: Number(e.target.value) })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>

              <div>
                <Label className="text-sm block">Fecha *</Label>
                <Input type="date" placeholder="Fecha (YYYY-MM-DD)" value={form.fecha || ""} onChange={(e) => setForm({ ...form, fecha: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>

              <div>
                <Label className="text-sm block">Estado</Label>
                <select className="mt-2 h-10 w-full rounded-full bg-white px-4 text-sm border-0 shadow-sm" value={form.estado} onChange={(e) => {
                  const v = e.target.value as any;
                  if (v === 'PAID') {
                    setForm((prev) => ({ ...prev, estado: v, fecha_pago: prev.fecha_pago || prev.fecha || today }));
                  } else {
                    setForm((prev) => ({ ...prev, estado: v, fecha_pago: undefined }));
                  }
                }} disabled={loading}>
                  <option value="PENDING">PENDING</option>
                  <option value="PAID">PAID</option>
                </select>
              </div>

              {form.estado === 'PAID' ? (
                <div>
                  <Label className="text-sm block">Fecha pago *</Label>
                  <Input placeholder="Fecha pago (obligatoria)" type="date" min={String(form.fecha || today)} value={form.fecha_pago || ""} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} required />
                </div>
              ) : (
                <div>
                  <Label className="text-sm block">Fecha pago (opcional)</Label>
                  <Input placeholder="Fecha pago (opcional)" type="date" value={form.fecha_pago || ""} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
                </div>
              )}

              <div>
                <Label className="text-sm block">Enlace (URL)</Label>
                <Input placeholder="https://..." type="url" value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} className="mt-2 h-10 rounded-full bg-white border-0 px-4 shadow-sm text-sm" disabled={loading} />
              </div>
            </div>
          </div>

          <DialogFooter className="pt-6 border-t border-gray-100 flex justify-between items-center">
            <div>
              <Button variant="outline" className="h-12 px-8" onClick={() => { if (loading) return; setIsEditOpen(false); setEditing(null); setForm({ estado: 'PENDING' }); }} disabled={loading}>Cancelar</Button>
            </div>
            <div>
              <Button variant="black" className="h-12 px-8" onClick={handleUpdate} disabled={loading}>{loading ? (<span className="inline-flex items-center gap-2"><Spinner size={14} /> Guardando…</span>) : 'Guardar cambios'}</Button>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default FeesManager;
